import {
  CheckmarkCircle24Filled,
  CheckmarkRegular,
  Circle24Filled,
  DismissCircleFilled,
  ErrorCircle24Filled,
} from '@fluentui/react-icons';
import cn from 'classnames';
import React, { KeyboardEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from '../../Loader/Loader';
import { SignOffPerson, SignOffState } from './SignOffButton.interfaces';
import './SignOffButton.scss';

export interface SignOffButtonProps {
  signOffState: SignOffState;
  meta?: string;
  onClick?: () => void;
  outline?: boolean;
  date?: string;
  isLoading?: boolean;
  personSignings?: SignOffPerson[];
}

export const SignOffButton: React.FC<SignOffButtonProps> = (props) => {
  const { t } = useTranslation();

  const signingPersonsListData = useMemo(
    () => (props.personSignings ? getSigningPersons(props.personSignings) : []),
    [props.personSignings],
  );

  const hasMultipleSignings = !!signingPersonsListData && signingPersonsListData.length > 1;
  const hasMetaData = !!props.meta && props.meta.length > 0;
  const hasSigningDate = !!props.date && props.date.length > 0;
  const isSignedOff = props.signOffState === SignOffState.SIGNED_OFF;

  const signerList = useMemo(
    () =>
      hasMultipleSignings && props.signOffState === SignOffState.NOT_SIGNED_OFF ? (
        <ul className="signingList">{signingPersonsListData}</ul>
      ) : null,
    [signingPersonsListData, props.signOffState],
  );

  const header = useMemo(() => {
    switch (props.signOffState) {
      case SignOffState.SIGNED_OFF:
        return (
          <div className="stateLabel">
            {t('classbook:sign_off_button:header:signed', { defaultValue: 'Abgezeichnet' })}
          </div>
        );
      case SignOffState.CONFIGURATION_ISSUE:
        return (
          <div className="stateLabel">
            {t('classbook:sign_off_button:header:configuration_issue', {
              defaultValue: 'Konfigurationsfehler',
            })}
          </div>
        );
      case SignOffState.NOT_SIGNED_OFF:
        return (
          <div className="stateLabel">
            {t('classbook:sign_off_button:header:not_signed', {
              defaultValue: 'Nicht Abgezeichnet',
            })}
          </div>
        );
      default:
        return (
          <div className="stateLabel">
            {t('classbook:sign_off_button:header:not_signed', {
              defaultValue: 'Nicht Abgezeichnet',
            })}
          </div>
        );
    }
  }, [props.signOffState, t]);

  const leadingIcon = useMemo(() => {
    switch (true) {
      case props.isLoading:
        return <Loader size="xs" className="signedOffIcon" />;
      case props.signOffState === SignOffState.SIGNED_OFF:
        return <CheckmarkCircle24Filled className="signedOffIcon" />;
      case props.signOffState === SignOffState.CONFIGURATION_ISSUE:
        return <ErrorCircle24Filled className="signedOffIcon" />;
      case props.signOffState === SignOffState.NOT_SIGNED_OFF:
        return (
          <Circle24Filled className={getSignOffButtonClass(props.personSignings ?? []).join(' ')} />
        );
      default:
        return (
          <Circle24Filled className={getSignOffButtonClass(props.personSignings ?? []).join(' ')} />
        );
    }
  }, [props.isLoading, props.signOffState, props.personSignings, t]);

  const getMetaText = useCallback((): string | undefined => {
    let text: string | undefined;
    if (hasMetaData) {
      text = props.meta;
      if ((!hasMultipleSignings && hasSigningDate) || (hasSigningDate && isSignedOff)) {
        text = `${text} ${props.date}`;
      }
    }
    return text;
  }, [hasMetaData, hasMultipleSignings, hasSigningDate, props.date, props.meta]);

  return (
    <div
      tabIndex={0}
      role={props.onClick && !props.isLoading ? 'button' : undefined}
      className={cn('flex flex-col signOffButton', {
        notSignedOff: props.signOffState === SignOffState.NOT_SIGNED_OFF,
        signedOff: props.signOffState === SignOffState.SIGNED_OFF,
        configurationIssue: props.signOffState === SignOffState.CONFIGURATION_ISSUE,
        outline: props.outline,
      })}
      onClick={props.isLoading ? undefined : props.onClick}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && props.onClick) {
          props.onClick();
        }
      }}
    >
      <div className="flex items-center headingSection">
        {leadingIcon}
        <div className="flex flex-col headerWithSubheader">
          {header}
          <div className="meta">{getMetaText()}</div>
        </div>
      </div>
      <div className="flex flex-col">{signerList}</div>
    </div>
  );
};

const getSigningPersons = (personSignings: SignOffPerson[]) =>
  personSignings?.map((personSigning, index) => {
    return (
      <li key={index}>
        <div className="flex items-center checkPersonListItem">
          {personSigning.signOffDate ? (
            <CheckmarkRegular className="checkmark" />
          ) : (
            <DismissCircleFilled className="dismissIcon" />
          )}
          <span className="meta">{getSigningListText(personSigning)}</span>
        </div>
      </li>
    );
  });

const getSigningListText = (personSigning: SignOffPerson) => {
  let content = `${personSigning.personDisplayName}`;
  if (personSigning.signOffDate) {
    content = `${content} (${personSigning.signOffDate})`;
  }
  return content;
};

const getSignOffButtonClass = (personSignings: SignOffPerson[]) => {
  const buttonColorClass = ['signedOffIcon'];
  const signedOffQuantity =
    personSignings?.filter((personSigning) => !!personSigning.signOffDate).length ?? 0;
  if (signedOffQuantity > 0) {
    buttonColorClass.push('incomplete');
  }
  return buttonColorClass;
};
